import React from 'react';
import { Autocomplete, TextField, Chip, Grid, Checkbox, InputAdornment, IconButton, Typography } from '@mui/material';
import { DateFilterFieldProps, FilterFieldProps, FilterValue, LabelValue } from '../../../Types/filter.types';
import ClearIcon from '@mui/icons-material/Clear';
interface FilterTextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

export const DATE_PRESET_OPTIONS = [
  'Today',
  'Yesterday',
  'This Week',
  'This Month',
  'Previous Month',
  'Custom',
] as const;

export type DatePresetType = typeof DATE_PRESET_OPTIONS[number];

const FilterTextField: React.FC<FilterTextFieldProps> = ({
  label,
  value,
  onChange,
  type = 'text',
  disabled = false,
  error,
  helperText,
  required,
}) => {
  return (
    <TextField
      size="small"
      fullWidth
      type={type}
      label={label}
      value={value || ''}
      disabled={disabled}
      error={error}
      helperText={helperText}
      InputLabelProps={{ shrink: true }}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      sx={{
        width: '100%',
        '& .MuiInputBase-root': {
          fontSize: '0.5rem',
          minHeight: '32px',
          padding: '1px 6px',
        },
        '& .MuiOutlinedInput-root': {
          paddingTop: '2px',
          paddingBottom: '2px',
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.8rem',
          transform: 'translate(12px, 7px) scale(1)',
        },
        '& .MuiInputLabel-shrink': {
          transform: 'translate(12px, -9px) scale(0.75)',
        },
        '& input': {
          fontSize: '0.7rem',
          padding: '1px 2px',
        },
      }}
    />
  );
};

const FilterField: React.FC<FilterFieldProps> = ({
  label,
  field,
  options,
  value,
  onChange,
  single
}) => {
  const [inputValue, setInputValue] = React.useState<string>('');

  const handleChange = (
    _: React.SyntheticEvent,
    newValue: LabelValue[],
    __: string,
    details?: { option: LabelValue }
  ): void => {
    const clicked: string | undefined = details?.option?.value;
    const allValues: string[] = options.map((o: LabelValue) => o.value);

    if (clicked === 'all' && safeValue.values.length === 0) {
      onChange(field, allValues, true);
      return;
    }

    if (clicked === 'all' && safeValue.values.length > 0) {
      onChange(field, [], false);
      return;
    }

    if (clicked === 'select-visible') {
      const visibleOptions: LabelValue[] = getFilteredOptions();
      const visibleValues: string[] = visibleOptions.map((opt: LabelValue) => opt.value); 
      if (visibleValues.length && visibleValues.every(v => safeValue.values.includes(v))) {
        const remainingValues: string[] = safeValue.values.filter(v => !visibleValues.includes(v));
        onChange(field, remainingValues, false);
      } else {
        const combinedValues: string[] = Array.from(new Set([...safeValue.values, ...visibleValues]));
        onChange(field, combinedValues, false);
      }
      return;
    }

    const selectedIds: string[] = newValue.map((v: LabelValue) => v.value);
    onChange(field, selectedIds, false);
  };

  const safeValue: FilterValue = React.useMemo((): FilterValue => {
    if (!value) return { values: [], isAll: false, isPartialAll: false };
    if (Array.isArray(value)) return { values: value, isAll: false };
    if (typeof value === 'object') {
      return {
        values: Array.isArray(value.values) ? value.values : [],
        isAll: !!value.isAll,
        isPartialAll: !!value.isPartialAll,
      };
    }
    return { values: [], isAll: false, isPartialAll: false };
  }, [value]);

  const autocompleteValue: LabelValue[] = React.useMemo((): LabelValue[] => {
    if (!safeValue.values.length) return [];
    return options.filter((opt: LabelValue) => safeValue.values.includes(opt.value));
  }, [safeValue.values, options]);

  const getFilteredOptions = (): LabelValue[] => {
    if (!inputValue.trim()) return options;
    const searchTerm: string = inputValue.toLowerCase();
    return options.filter((opt: LabelValue) =>
      opt.label.toLowerCase().includes(searchTerm)
    );
  };

  const filteredOptions: LabelValue[] = getFilteredOptions();
  const hasSearchInput: boolean = inputValue.trim().length > 0;
  const hasAnySelection: boolean = safeValue.values.length > 0;

  const allVisibleSelected: boolean =
    filteredOptions.length > 0 &&
    filteredOptions.every((opt: LabelValue) => safeValue.values.includes(opt.value));

  const allLabel: string = safeValue.isAll || hasAnySelection ? 'select none' : 'select all';
  const selectVisibleLabel: string = allVisibleSelected ? 'deselect visible' : 'select visible';

  const optionsWithSelectAll: LabelValue[] = [
    ...(hasSearchInput
      ? [{ label: selectVisibleLabel, value: "select-visible" }]
      : [{ label: allLabel, value: "all" }]
    ),
    ...filteredOptions
  ];

  return (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      options={optionsWithSelectAll}
      value={autocompleteValue}
      onChange={handleChange}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      filterOptions={(opts, state) => {
        return opts;
      }}
      renderOption={(props, option) => {
        const isAllOption = option.value === 'all';
        const isSelectVisibleOption : boolean = option.value === 'select-visible';

        return (
          <li {...props} style={{ padding: "2px 8px", minHeight: 20 }}>
            {isAllOption || isSelectVisibleOption ? (
              <Typography
                sx={{
                  fontSize: '0.70rem',
                  fontWeight: 600,
                  color: 'primary.main',
                  cursor: 'pointer',
                }}
              >
                {option.label}
              </Typography>
            ) : (
              <>
                <Checkbox
                  size="small"
                  sx={{ mr: 0.5, p: "2px" }}
                  checked={safeValue.values.includes(option.value)}
                />
                <Typography sx={{ fontSize: '0.7rem', cursor: 'pointer', color: "black" }}>
                  {option.label}
                </Typography>
              </>
            )}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={`Select ${label}`} />
      )}
      renderTags={(selected: LabelValue[], getTagProps) => {
        const visibleTags: LabelValue[] = selected.slice(0, 2);
        const extraCount: number = selected.length - 2;

        return (
          <>
            {visibleTags.map((option: LabelValue, index: number) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  maxWidth: "80px",
                  '& .MuiChip-label': {
                    padding: '0 6px',
                  },
                }}
              />
            ))}
            {extraCount > 0 && (
              <Chip
                label={`+${extraCount} more`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  backgroundColor: "#eee",
                }}
              />
            )}
          </>
        );
      }}
      sx={{
        width: '100%',
        '& .MuiInputBase-root': {
          fontSize: '0.5rem',
          minHeight: '32px',
          padding: '1px 6px',
        },
        '& .MuiOutlinedInput-root': {
          paddingTop: '2px',
          paddingBottom: '2px',
        },
        '& .MuiAutocomplete-input': {
          fontSize: '0.7rem',
          padding: '1px 2px !important',
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.8rem',
          transform: 'translate(12px, 7px) scale(1)',
        },
        '& .MuiInputLabel-shrink': {
          transform: 'translate(12px, -9px) scale(0.75)',
        },
        '& .MuiAutocomplete-tag': {
          maxWidth: '80px',
          margin: '1px',
          fontSize: '0.65rem',
        },
      }}
      componentsProps={{
        popper: {
          sx: {
            '& .MuiAutocomplete-listbox': {
              fontSize: '0.7rem',
              padding: '2px 0',
              '& .MuiAutocomplete-option': {
                minHeight: '24px',
                fontSize: '0.7rem',
                padding: '2px 8px',
                lineHeight: 1.2,
              },
            },
          },
        },
      }}
    />
  );
};

const DateFilterField: React.FC<DateFilterFieldProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  error,
  helperText,
}) => {
  return (
    <TextField
      type="date"
      size="small"
      fullWidth
      label={label}
      value={value || ''}
      disabled={disabled}
      error={error}
      helperText={helperText}
      InputLabelProps={{ shrink: true }}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      sx={{
        width: '100%',
        '& .MuiInputBase-root': {
          fontSize: '0.5rem',
          minHeight: '32px',
          padding: '1px 6px',
        },
        '& .MuiOutlinedInput-root': {
          paddingTop: '2px',
          paddingBottom: '2px',
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.8rem',
          transform: 'translate(12px, 7px) scale(1)',
        },
        '& .MuiInputLabel-shrink': {
          transform: 'translate(12px, -9px) scale(0.75)',
        },
        '& input': {
          fontSize: '0.7rem',
          padding: '1px 2px',
        },
      }}
    />
  );
};

interface PresetFilterFieldProps {
  label: string;
  value: DatePresetType | null;
  onChange: (value: DatePresetType | null) => void;
}

const PresetFilterField: React.FC<PresetFilterFieldProps> = ({
  label,
  value,
  onChange,
}) => {
  return (
    <Autocomplete<DatePresetType>
      size="small"
      options={[...DATE_PRESET_OPTIONS]}
      value={value}
      onChange={(_: React.SyntheticEvent, newValue: DatePresetType | null) => onChange(newValue)}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={`Select ${label}`} />
      )}
      sx={{
        width: '100%',
        '& .MuiInputBase-root': {
          fontSize: '0.5rem',
          minHeight: '32px',
          padding: '1px 6px',
        },
        '& .MuiOutlinedInput-root': {
          paddingTop: '2px',
          paddingBottom: '2px',
        },
        '& .MuiAutocomplete-input': {
          fontSize: '0.7rem',
          padding: '1px 2px !important',
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.8rem',
          transform: 'translate(12px, 7px) scale(1)',
        },
        '& .MuiInputLabel-shrink': {
          transform: 'translate(12px, -9px) scale(0.75)',
        },
        '& .MuiAutocomplete-tag': {
          maxWidth: '80px',
          margin: '1px',
          fontSize: '0.65rem',
        },
      }}
      componentsProps={{
        popper: {
          sx: {
            '& .MuiAutocomplete-listbox': {
              fontSize: '0.7rem',
              padding: '2px 0',
              '& .MuiAutocomplete-option': {
                minHeight: '24px',
                fontSize: '0.7rem',
                padding: '2px 8px',
                lineHeight: 1.2,
              },
            },
          },
        },
      }}
    />
  );
};

interface NumberRangeFieldProps {
  labelStart: string;
  labelEnd: string;
  startValue?: string;
  endValue?: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
}

const NumberRangeField: React.FC<NumberRangeFieldProps> = ({
  labelStart,
  labelEnd,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  step = 1,
  min,
  max,
  disabled = false,
}) => {
  const commonSx = {
    width: '100%',
    '& .MuiInputBase-root': {
      fontSize: '0.5rem',
      minHeight: '32px',
      padding: '1px 6px',
    },
    '& .MuiOutlinedInput-root': {
      paddingTop: '2px',
      paddingBottom: '2px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.8rem',
      transform: 'translate(12px, 7px) scale(1)',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(12px, -9px) scale(0.75)',
    },
    '& input': {
      fontSize: '0.7rem',
      padding: '1px 2px',
      textAlign: 'right',
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      transform: 'scale(0.7)',
    },
  };

  const hasError: boolean = !!(startValue && endValue && Number(endValue) < Number(startValue));

  return (
    <>
      <Grid item xs={6}>
        <TextField
          type="number"
          size="small"
          fullWidth
          label={labelStart}
          value={startValue || ''}
          disabled={disabled}
          inputProps={{ step, min, max }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onStartChange(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {startValue && !disabled && (
                  <IconButton
                    size="small"
                    onClick={(): void => onStartChange('')}
                    edge="end"
                    sx={{
                      padding: '2px',
                      marginRight: '-3px',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={commonSx}
        />
      </Grid>

      <Grid item xs={6}>
        <TextField
          type="number"
          size="small"
          fullWidth
          label={labelEnd}
          value={endValue || ''}
          disabled={disabled}
          error={hasError}
          inputProps={{ step, min, max }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onEndChange(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {endValue && !disabled && (
                  <IconButton
                    size="small"
                    onClick={(): void => onEndChange('')}
                    edge="end"
                    sx={{
                      padding: '2px',
                      marginRight: '-3px',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={commonSx}
        />
      </Grid>
    </>
  );
};

interface FilterAutocompleteProps {
  label: string;
  options: LabelValue[];
  value: string;
  onChange: (value: string) => void;
  width?: number | string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  openOnFocus?: boolean;
  disabled?: boolean;
}

const FilterAutocomplete: React.FC<FilterAutocompleteProps> = ({
  label,
  options,
  value,
  onChange,
  width = "100%",
  placeholder,
  required, 
  error,
  helperText,
  openOnFocus = false,
  disabled = false,
}) => {
  const [open, setOpen] = React.useState(false);

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      value={options.find((opt: LabelValue) => opt.value === value) || null}
      open={openOnFocus ? open : undefined}
      onOpen={() => openOnFocus && setOpen(true)}
      onClose={() => openOnFocus && setOpen(false)}
      onChange={(_: React.SyntheticEvent, newValue: LabelValue | null): void => {
        onChange(newValue ? newValue.value : '');
        if (openOnFocus) setOpen(false);
      }}
      getOptionLabel={(option: LabelValue) => option.label}
      isOptionEqualToValue={(option: LabelValue, val: LabelValue) => option.value === val.value}
      renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: LabelValue) => (
        <li {...props} style={{ padding: '2px 8px', minHeight: 20, whiteSpace: 'nowrap' }}>
          <Typography sx={{ fontSize: '0.7rem', cursor: 'pointer', color: 'black', whiteSpace: 'nowrap' }}>
            {option.label}
          </Typography>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          placeholder={placeholder || `Select ${label}`}
          onFocus={() => openOnFocus && setOpen(true)}
          inputProps={{
            ...params.inputProps,
            placeholder: placeholder || `Select ${label}`,
          }}
        />
      )}
      sx={{
        width,
        "& .MuiInputBase-root": {
          fontSize: "0.5rem",
          minHeight: "32px",
          padding: "1px 6px",
        },
        "& .MuiOutlinedInput-root": {
          paddingTop: "2px",
          paddingBottom: "2px",
        },
        "& .MuiAutocomplete-input": {
          fontSize: "0.7rem",
          padding: "1px 2px !important",
        },
        "& .MuiInputLabel-root": {
          fontSize: "0.8rem",
          transform: "translate(12px, 7px) scale(1)",
        },
        "& .MuiInputLabel-shrink": {
          transform: "translate(12px, -9px) scale(0.75)",
        },
        "& input::placeholder": {
          fontSize: "0.7rem",
          opacity: 0.6,
        },
      }}
      componentsProps={{
        popper: {
          style: { width: "auto", minWidth: "100px" },
          placement: "bottom-start",
          sx: {
            "& .MuiAutocomplete-paper": {
              marginTop: "4px",
            },
            "& .MuiAutocomplete-listbox": {
              fontSize: "0.7rem",
              padding: "6px 4px",
              whiteSpace: "nowrap",
              "& .MuiAutocomplete-option": {
                minHeight: "24px",
                fontSize: "0.7rem",
                padding: "4px 8px",
                margin: "2px 0",
                borderRadius: "4px",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              },
            },
          },
        },
      }} />
  );
};



export { DateFilterField, PresetFilterField, NumberRangeField, FilterAutocomplete, FilterTextField };
export default FilterField;